/**
 * Questionnaire body — switches between stage Rives and the scrolling questions list.
 *
 * Belongs here: body slot from `bodyPhase` (including last-question leave via ContentSlideOut).
 * Does not belong here: answer handling or which question is active (see QuestionsContent).
 */
import { getBottomPadding } from '../../../../../utils/sessionStorage'
import { ContentSlideOut, SlideDirection } from '../../shared/transitions/DirectionalSlideSwap'
import {
  STAGE_COMPLETE_QUESTIONS_EXIT_OUT_MS,
  STAGE_COMPLETION_RIVE_SRC,
  STAGE_COMPLETION_RIVE_TIMEOUT_MS,
  STAGE_INTRO_RIVE_TIMEOUT_MS
} from '../setup/config'
import { QuestionnaireBodyPhase } from './useStageAnimations'
import { StageRiveWrapper } from './StageRiveWrapper'
import type { QuestionsContentProps } from './QuestionsContent'
import { QuestionsContent } from './QuestionsContent'

export interface StrategyQuestionnaireBodyProps {
  bodyPhase: QuestionnaireBodyPhase
  stageIntroRiveSrc: string | undefined
  stageIntroStageId: string | undefined
  slideDirection?: SlideDirection
  onStageIntroComplete: () => void
  onStageCompleteRiveDone: () => void
  questionsContent: QuestionsContentProps
}

export function StrategyQuestionnaireBody({
  bodyPhase,
  stageIntroRiveSrc,
  stageIntroStageId,
  slideDirection = SlideDirection.Forward,
  onStageIntroComplete,
  onStageCompleteRiveDone,
  questionsContent
}: StrategyQuestionnaireBodyProps) {
  if (bodyPhase === QuestionnaireBodyPhase.StageIntro && stageIntroRiveSrc) {
    return (
      <div className='flex flex-1 min-h-0 flex-col bg-[#F0F4F7]'>
        <StageRiveWrapper
          // key must change per stage so Rive remounts after stage-complete animation
          key={`intro-${stageIntroStageId ?? stageIntroRiveSrc}`}
          riveSrc={stageIntroRiveSrc}
          timeoutMs={STAGE_INTRO_RIVE_TIMEOUT_MS}
          onComplete={onStageIntroComplete}
        />
      </div>
    )
  }

  if (bodyPhase === QuestionnaireBodyPhase.StageComplete) {
    return (
      <div className='flex flex-1 min-h-0 flex-col'>
        <StageRiveWrapper
          key='stage-complete'
          riveSrc={STAGE_COMPLETION_RIVE_SRC}
          timeoutMs={STAGE_COMPLETION_RIVE_TIMEOUT_MS}
          onComplete={onStageCompleteRiveDone}
        />
      </div>
    )
  }

  return (
    <div className='flex-1 min-h-0 overflow-y-auto no-scrollbar'>
      {/* Always mount ContentSlideOut shell — only `active` flips, so QuestionsContent does not remount */}
      <ContentSlideOut
        active={bodyPhase === QuestionnaireBodyPhase.QuestionsLeaving}
        direction={slideDirection}
        outDurationMs={STAGE_COMPLETE_QUESTIONS_EXIT_OUT_MS}
      >
        <QuestionsContent {...questionsContent} />
      </ContentSlideOut>
    </div>
  )
}

export function getQuestionnaireShellBottomPadding(): number {
  return getBottomPadding() + 16
}
